// src/plugins/sales-forecast/index.tsx
import React, { useMemo, useState, useCallback, useEffect } from "react";
import { ControlPanel, Params } from "./components/ControlPanel";
import pluginConfig from "./sales-plugin.json";
import { centroids, clusterInfo } from "./clusterConfig";
import { DomPredictionPanel } from "./components/DomPredictionPanel";
import { ConstructionPhase } from "./components/ConstructionPhaseControl";
import { MarketAbsorptionChart } from "./components/MarketAbsorptionChart";
import { MarketForecastPanel } from "./components/MarketForecastPanel";
import { AmenityOutput } from "./components/AmenityOutput";
import { client } from "@sigmacomputing/plugin";
import { useSigmaElementData } from "./lib/useSigmaElementData";
import type { CloneEntry } from "./components/CloneNeighborhoodControl";
import { useLightDarkMode } from "./components/LightDarkModeSwitch";
import "./styles/theme.css";

// Chart
import { ConstructionPipelineChart } from "./components/ConstructionPipelineChart";

client.config.configureEditorPanel([
  { name: "ehMaster", type: "element", label: "EH MASTER" },
  { name: "addressCol", type: "column", source: "ehMaster", label: "Address", allowMultiple: false },
  { name: "cityCol", type: "column", source: "ehMaster", label: "City", allowMultiple: false },
  { name: "neighborhoodCol", type: "column", source: "ehMaster", label: "Neighborhood", allowMultiple: false },
  { name: "neighborhoodGroupCol", type: "column", source: "ehMaster", label: "Neighborhood - Grouped", allowMultiple: false },
  { name: "houseTypeCol", type: "column", source: "ehMaster", label: "House Type", allowMultiple: false },
  { name: "propertyTypeCol", type: "column", source: "ehMaster", label: "Property Type", allowMultiple: false },
  { name: "contractDateCol", type: "column", source: "ehMaster", label: "Contract Date", allowMultiple: false },
  { name: "officeGreenLightCol", type: "column", source: "ehMaster", label: "Office Green Light", allowMultiple: false },
  { name: "activeCompsCol", type: "column", source: "ehMaster", label: "Active Comps", allowMultiple: false },
  { name: "marketCol", type: "column", source: "ehMaster", label: "Market", allowMultiple: false },
  { name: "schoolDistrictCol", type: "column", source: "ehMaster", label: "School District", allowMultiple: false },
  { name: "floorPlanCol", type: "column", source: "ehMaster", label: "Floor Plan", allowMultiple: false },
  { name: "styleCol", type: "column", source: "ehMaster", label: "Style", allowMultiple: false },
  { name: "priceCol", type: "column", source: "ehMaster", label: "Price", allowMultiple: false },
  { name: "sqftCol", type: "column", source: "ehMaster", label: "Square Feet", allowMultiple: false },
]);

const DEFAULT_PARAMS: Params = {
  interestRate: pluginConfig.defaults.interestRate,
  interestRateThreeMonthChange: pluginConfig.defaults.interestRateThreeMonthChange,
  interestRateSixMonthChange: pluginConfig.defaults.interestRateSixMonthChange,
  interestRateNineMonthChange: pluginConfig.defaults.interestRateNineMonthChange,
  interestRateTwelveMonthChange: pluginConfig.defaults.interestRateTwelveMonthChange,
  interestRateTwoYearChange: pluginConfig.defaults.interestRateTwoYearChange,
  initialSpecs: pluginConfig.defaults.initialSpecs,
  specsToStart: pluginConfig.defaults.specsToStart,
  perSpecsSold: pluginConfig.defaults.perSpecsSold,
  preSolds: pluginConfig.defaults.preSolds,
  avgPriceMin: pluginConfig.defaults.avgPriceMin,
  avgPriceMax: pluginConfig.defaults.avgPriceMax,
  availableLots: pluginConfig.defaults.availableLots,
  squareFootMin: pluginConfig.defaults.squareFootMin,
  squareFootMax: pluginConfig.defaults.squareFootMax,
  compositionPct: pluginConfig.defaults.composition * 100,
  amenities: [...pluginConfig.defaults.amenities],
  threeMonthPriceChange: pluginConfig.defaults.threeMonthPriceChange,
  sixMonthPriceChange: pluginConfig.defaults.sixMonthPriceChange,
  nineMonthPriceChange: pluginConfig.defaults.nineMonthPriceChange,
  twelveMonthPriceChange: pluginConfig.defaults.twelveMonthPriceChange,
  twoYearPriceChange: pluginConfig.defaults.twoYearPriceChange,
  bedrooms: pluginConfig.defaults.bedrooms,
  baths: pluginConfig.defaults.baths,
  stories: pluginConfig.defaults.stories,
  yearBuilt: pluginConfig.defaults.yearBuilt,
  garageType: pluginConfig.defaults.garageType,
  garageSpaces: pluginConfig.defaults.garageSpaces,
  city: pluginConfig.defaults.city,
  lotType: pluginConfig.defaults.lotType,
  listingDay: pluginConfig.defaults.listingDay,
  listingMonth: pluginConfig.defaults.listingMonth,
  activeComps: pluginConfig.defaults.activeComps,
  neighborhoodID: pluginConfig.defaults.id,
  neighborhoodTitle: pluginConfig.defaults.title,
  status: pluginConfig.defaults.title,
  cloneType: pluginConfig.defaults.cloneType,
  cloneDate: new Date().toLocaleDateString("en-CA", { timeZone: "America/Chicago" }),
  constructionPhases: [...(pluginConfig.defaults.constructionPhases as ConstructionPhase[])],
  cloneFlag: false,
};

function paramsFromClone(entry: CloneEntry, prev: Params): Params {
  const d = entry.defaults;
  return {
    ...prev,
    neighborhoodID: entry.id,
    neighborhoodTitle: entry.title,
    cloneType: entry.cloneType,
    status: entry.status,
    interestRate: prev.interestRate,
    interestRateThreeMonthChange: prev.interestRate,
    interestRateSixMonthChange: prev.interestRate,
    interestRateNineMonthChange: prev.interestRate,
    interestRateTwelveMonthChange: prev.interestRate,
    interestRateTwoYearChange: prev.interestRate,
    initialSpecs: d.initialSpecs,
    preSolds: d.preSolds,
    specsToStart: d.specsToStart,
    perSpecsSold: d.perSpecsSold,
    avgPriceMin: d.avgPriceMin,
    avgPriceMax: d.avgPriceMax,
    availableLots: d.availableLots,
    squareFootMin: d.squareFootMin,
    squareFootMax: d.squareFootMax,
    compositionPct: (d.composition ?? 0) * 100,
    amenities: [...(d.amenities ?? [])],
    threeMonthPriceChange: d.threeMonthPriceChange,
    sixMonthPriceChange: d.sixMonthPriceChange,
    nineMonthPriceChange: d.nineMonthPriceChange,
    twelveMonthPriceChange: d.twelveMonthPriceChange,
    twoYearPriceChange: d.twoYearPriceChange,
    bedrooms: d.bedrooms,
    baths: d.baths,
    stories: d.stories,
    yearBuilt: d.yearBuilt,
    garageType: d.garageType,
    garageSpaces: d.garageSpaces,
    city: d.city,
    lotType: d.lotType,
    listingDay: d.listingDay,
    listingMonth: d.listingMonth,
    activeComps: d.activeComps,
    cloneDate: d.cloneDate ?? prev.cloneDate,
    constructionPhases: [...(pluginConfig.defaults.constructionPhases as ConstructionPhase[])],
    cloneFlag: false,
  };
}

// ── Rate API helpers ──────────────────────────────────────────────────────────
const API_BASE = process.env.REACT_APP_API_BASE ?? "";

function parseLocalYMD(ymd: string): Date {
  const [y, m, d] = ymd.split("-").map(Number);
  return new Date(y, (m ?? 1) - 1, d ?? 1);
}

async function fetchRateForDate(targetDate?: string): Promise<number | undefined> {
  try {
    const url = targetDate
      ? `${API_BASE}/latest-rate?as_of=${targetDate}`
      : `${API_BASE}/latest-rate`;

    const res = await fetch(url, { cache: "no-store" });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);

    const json = await res.json();
    const { rate } = json;
    if (typeof rate === "number" && isFinite(rate) && rate > 0) {
      return rate;
    }
  } catch (err) {
    console.error("[fetchRateForDate] Error:", err);
  }
  return undefined;
}

// ✅ Computes all offset rates for a given cloneDate in one pass.
// Returns pluginConfig.defaults for any offset whose target date is in the future.
async function computeOffsetRates(cloneDate: string): Promise<Record<string, number>> {
  const baseDate = parseLocalYMD(cloneDate);
  const today = new Date();
  const addMonths = (d: Date, m: number) =>
    new Date(d.getFullYear(), d.getMonth() + m, d.getDate());

  const offsets = [
    { key: "interestRateThreeMonthChange", months: 3 },
    { key: "interestRateSixMonthChange", months: 6 },
    { key: "interestRateNineMonthChange", months: 9 },
    { key: "interestRateTwelveMonthChange", months: 12 },
    { key: "interestRateTwoYearChange", months: 24 },
  ] as const;

  const results: Record<string, number> = {};
  for (const { key, months } of offsets) {
    const target = addMonths(baseDate, months);
    if (target > today) {
      results[key] = (pluginConfig.defaults as any)[key];
    } else {
      const rate = await fetchRateForDate(target.toISOString().split("T")[0]);
      results[key] =
        typeof rate === "number" && isFinite(rate) && rate > 0
          ? Number(rate.toFixed(3))
          : (pluginConfig.defaults as any)[key];
    }
  }
  return results;
}
// ─────────────────────────────────────────────────────────────────────────────

type ForecastRecord = {
  date: string;
  presolds: number;
  sold_specs: number;
  unsold_specs: number;
  available_lots: number;
};

export type ServerComposition =
  | {
      spec_pct?: number | string | null;
      presold_pct?: number | string | null;
      specPct?: number | string | null;
      presoldPct?: number | string | null;
      comp_fraction?: number | string | null;
      compFraction?: number | string | null;
    }
  | null;

type ChartViewMode = "marketAbsorption" | "constructionPipeline";

export function SalesForecast() {
  const [params, setParams] = useState<Params>({ ...DEFAULT_PARAMS });

  const [triggerDom, setTriggerDom] = useState(false);
  const [triggerForecast, setTriggerForecast] = useState(false);

  const [forecastData, setForecastData] = useState<ForecastRecord[]>([]);
  const [pipelineData, setPipelineData] = useState<any>(null);

  const [domPredictions, setDomPredictions] =
    useState<{ [k: string]: number | null } | null>(null);

  const [selectedClone, setSelectedClone] = useState<CloneEntry | null>(null);
  const [composition, setComposition] = useState<ServerComposition>(null);

  const { theme, toggleTheme } = useLightDarkMode();
  const elementRows = useSigmaElementData();
  const [chartView, setChartView] = useState<ChartViewMode>("marketAbsorption");

  // 🔹 On mount: fetch current rate AND offset rates atomically in a single setParams call.
  // This prevents InterestRateControl from ever seeing a mixed state where interestRate
  // has updated to the live value but the offsets are still stale.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const cloneDate = new Date().toLocaleDateString("en-CA", { timeZone: "America/Chicago" });
      const [latest, offsets] = await Promise.all([
        fetchRateForDate(),
        computeOffsetRates(cloneDate),
      ]);
      if (!cancelled && typeof latest === "number") {
        setParams((p) => ({
          ...p,
          interestRate: latest,
          ...offsets,
        }));
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // 🔹 When cloneDate changes, recompute offset rates
  useEffect(() => {
    if (!params.cloneDate) return;
    let cancelled = false;
    (async () => {
      const offsets = await computeOffsetRates(params.cloneDate);
      if (!cancelled) {
        setParams((prev) => ({ ...prev, ...offsets }));
      }
    })();
    return () => { cancelled = true; };
  }, [params.cloneDate]);

  const handleReset = useCallback(() => {
    setDomPredictions(null);
    setComposition(null);
    setForecastData([]);
    setPipelineData(null);
    setChartView("marketAbsorption");

    setParams((prev) => {
      if (selectedClone) {
        return paramsFromClone(selectedClone, prev);
      }
      console.warn("Reset called but no clone selected");
      return prev;
    });
  }, [selectedClone]);

  const handleForecastClick = useCallback(() => {
    setTriggerDom(true);
    setTriggerForecast(true);
  }, []);

  const handleCloneSelected = useCallback((entry: CloneEntry) => {
    console.log("[SalesForecast] Clone selected:", entry.title);
    setSelectedClone(entry);
    setParams((prev) => paramsFromClone(entry, prev));
    setComposition(null);
    setDomPredictions(null);
    setForecastData([]);
    setPipelineData(null);
  }, []);

  function squaredDistance(a: number[], b: number[]): number {
    return a.reduce((sum, val, i) => sum + Math.pow(val - b[i], 2), 0);
  }

  const memoizedParams = useMemo(() => ({ ...params }), [params]);

  const featureVector = useMemo<number[]>(() => {
    const pond = memoizedParams.amenities.includes("Pond");
    const largeLots = memoizedParams.amenities.includes("Large Lots");
    const gated = memoizedParams.amenities.includes("Gated");
    const pool = memoizedParams.amenities.includes("Pool");
    const clubhouse = memoizedParams.amenities.includes("Clubhouse");
    const kidsRec =
      memoizedParams.amenities.includes("Playground") ||
      memoizedParams.amenities.includes("Splash Pad");
    const adultsRec = ["Tennis", "Basketball", "Pickleball", "Walking Trails"].some((a) =>
      memoizedParams.amenities.includes(a)
    );
    return [
      pond ? 1 : 0,
      largeLots ? 1 : 0,
      gated ? 1 : 0,
      pool ? 1 : 0,
      clubhouse ? 1 : 0,
      kidsRec ? 1 : 0,
      adultsRec ? 1 : 0,
    ];
  }, [memoizedParams]);

  const cluster = useMemo<number>(() => {
    let best = 0;
    let bestDist = Infinity;
    centroids.forEach((c, i) => {
      const dist = squaredDistance(featureVector, c);
      if (dist < bestDist) {
        bestDist = dist;
        best = i;
      }
    });
    return best;
  }, [featureVector]);

  const clusterName = clusterInfo[cluster].name;
  const clusterDescription = clusterInfo[cluster].description;

  return (
    <div
      className={`plugin-root theme-${theme}`}
      style={{
        backgroundColor: "var(--background-color)",
        color: "var(--text-color)",
        minHeight: "100vh",
        padding: "1rem",
      }}
    >
      <div className="flex gap-6 items-start">
        <aside className="w-96 shrink-0">
          <ControlPanel
            params={params}
            setParams={setParams}
            resetDefaults={handleReset}
            onForecastClick={handleForecastClick}
            onCloneSelected={(entry) => handleCloneSelected(entry)}
          />
        </aside>

        <main className="max-h-96 flex-1 flex flex-col gap-6">
          <div className="mt-4">
            <MarketForecastPanel
              params={memoizedParams}
              cluster={cluster}
              trigger={triggerForecast}
              setTrigger={setTriggerForecast}
              forecastData={forecastData}
              setForecastData={setForecastData}
              pipelineData={pipelineData}
              setPipelineData={setPipelineData}
              domPredictions={domPredictions}
              elementRows={elementRows}
              selectedClone={selectedClone}
              setComposition={setComposition}
              theme={theme}
              onToggleTheme={toggleTheme}
            />

            {/* chart toggle */}
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                marginTop: "0.75rem",
                marginBottom: "0.75rem",
              }}
            >
              <div
                style={{
                  display: "inline-flex",
                  borderRadius: "999px",
                  border: "1px solid var(--border-color, #ccc)",
                  overflow: "hidden",
                  backgroundColor: "var(--control-bg, rgba(0,0,0,0.03))",
                }}
              >
                <button
                  type="button"
                  onClick={() => setChartView("marketAbsorption")}
                  style={{
                    padding: "0.35rem 0.9rem",
                    fontSize: "0.85rem",
                    fontWeight: 600,
                    border: "none",
                    cursor: "pointer",
                    backgroundColor:
                      chartView === "marketAbsorption"
                        ? "var(--accent-color, #2563eb)"
                        : "transparent",
                    color:
                      chartView === "marketAbsorption"
                        ? "var(--accent-foreground, #ffffff)"
                        : "var(--text-color)",
                    transition: "background-color 150ms ease, color 150ms ease",
                  }}
                >
                  Market Absorption
                </button>

                <button
                  type="button"
                  onClick={() => setChartView("constructionPipeline")}
                  style={{
                    padding: "0.35rem 0.9rem",
                    fontSize: "0.85rem",
                    fontWeight: 600,
                    border: "none",
                    cursor: "pointer",
                    backgroundColor:
                      chartView === "constructionPipeline"
                        ? "var(--accent-color, #2563eb)"
                        : "transparent",
                    color:
                      chartView === "constructionPipeline"
                        ? "var(--accent-foreground, #ffffff)"
                        : "var(--text-color)",
                    transition: "background-color 150ms ease, color 150ms ease",
                  }}
                >
                  Construction Pipeline
                </button>
              </div>
            </div>

            {chartView === "marketAbsorption" ? (
              <MarketAbsorptionChart
                key={`absorption-${forecastData.length}`}
                data={forecastData}
                cloneDate={params.cloneDate}
              />
            ) : (
              <ConstructionPipelineChart
                constructionPipeline={pipelineData}
                cloneDate={params.cloneDate}
                phases={params.constructionPhases}
                onChangePhases={(phases: ConstructionPhase[]) =>
                  setParams((p) => ({ ...p, constructionPhases: phases }))
                }
              />
            )}
          </div>
        </main>

        <aside className="w-80 flex flex-col gap-4">
          <AmenityOutput
            onChange={(updatedAmenities) =>
              setParams((prev) => ({ ...prev, amenities: updatedAmenities }))
            }
            clusterName={clusterName}
            clusterDescription={clusterDescription}
            cluster={cluster}
            composition={composition}
            forecastData={forecastData}
            params={memoizedParams}
            elementRows={elementRows}
          />
          <DomPredictionPanel
            params={memoizedParams}
            trigger={triggerDom}
            setTrigger={setTriggerDom}
            setDomPredictions={setDomPredictions}
          />
        </aside>
      </div>
    </div>
  );
}